// cartographer/spec — single source of truth for the architecture-
// mapping protocol. The deterministic engine (`repoToWorkflow`,
// `cartographerOverview`), the manual AI prompt (`AiJsonDialog`), and
// the in-app diagram authoring rules all import from here. When any of
// these numbers change, edit only this file.
//
// References:
// - ARCHITECTURE_MAPPING_HANDOVER.md (the cartographer protocol)
// - documents/v1/AI_JSON_REFERENCE_UPGRADE_PLAN.md (the rollout plan)

// ── Palette ──────────────────────────────────────────────────────────
//
// One row per architectural domain. `iconKebab` is the lowercase-kebab
// lucide name (compact-schema convention); `iconPascal` is the same icon
// in PascalCase (lean-1-schema convention). Hex chosen for legibility
// in both light and dark mode.

export interface CartographerPaletteRow {
  /** Bucket key — what `classifyFile()` returns. */
  key: string;
  /** Hex color used everywhere (UI swatches, block.color, group.color). */
  color: string;
  /** Human-readable label shown in group titles + dialog rendering. */
  label: string;
  /** Icon name for the COMPACT schema (lowercase-kebab). */
  iconKebab: string;
  /** Icon name for the LEAN-1 schema (PascalCase). */
  iconPascal: string;
}

export const CARTOGRAPHER_PALETTE: readonly CartographerPaletteRow[] = [
  { key: 'pages',      color: '#ec4899', label: 'Pages',      iconKebab: 'monitor',       iconPascal: 'Monitor' },
  { key: 'components', color: '#8b5cf6', label: 'Components', iconKebab: 'layers',        iconPascal: 'Layers' },
  { key: 'hooks',      color: '#a855f7', label: 'Hooks',      iconKebab: 'git-branch',    iconPascal: 'GitBranch' },
  { key: 'contexts',   color: '#0ea5e9', label: 'Contexts',   iconKebab: 'globe',         iconPascal: 'Globe' },
  { key: 'lib',        color: '#06b6d4', label: 'Libraries',  iconKebab: 'package',       iconPascal: 'Package' },
  { key: 'ai',         color: '#f97316', label: 'AI',         iconKebab: 'sparkles',      iconPascal: 'Sparkles' },
  { key: 'supabase',   color: '#22c55e', label: 'Backend',    iconKebab: 'server',        iconPascal: 'Server' },
  { key: 'types',      color: '#64748b', label: 'Types',      iconKebab: 'file-text',     iconPascal: 'FileText' },
  { key: 'styles',     color: '#f59e0b', label: 'Styles',     iconKebab: 'palette',       iconPascal: 'Palette' },
  { key: 'scripts',    color: '#94a3b8', label: 'Scripts',    iconKebab: 'terminal',      iconPascal: 'Terminal' },
  { key: 'tests',      color: '#10b981', label: 'Tests',      iconKebab: 'check-circle',  iconPascal: 'CheckCircle' },
  { key: 'docs',       color: '#737373', label: 'Docs',       iconKebab: 'book',          iconPascal: 'Book' },
  { key: 'infra',      color: '#ef4444', label: 'Infra',      iconKebab: 'settings',      iconPascal: 'Settings' },
  { key: 'misc',       color: '#71717a', label: 'Misc',       iconKebab: 'box',           iconPascal: 'Box' },
] as const;

/** Canonical render order — pages first (highest signal), misc last. */
export const CARTOGRAPHER_ORDER: ReadonlyArray<CartographerPaletteRow['key']> =
  CARTOGRAPHER_PALETTE.map((r) => r.key);

/** Lookup helper. Returns the row or the `misc` row as a safe fallback. */
export function paletteRow(key: string): CartographerPaletteRow {
  return (
    CARTOGRAPHER_PALETTE.find((r) => r.key === key) ??
    CARTOGRAPHER_PALETTE.find((r) => r.key === 'misc')!
  );
}

// ── Detail levels ────────────────────────────────────────────────────
//
// The Detaljnivå UI control (overview / standard / detailed) and the
// equivalent "Översikt / Standard / Detaljerad" labels in the handover
// spec map to these tuples. Values match the handover's §0 guidance
// ("Översikt ≈ 3, Standard ≈ 5, Detaljerad ≈ 7-8. Never exceed 8 per
// group.") with one deliberate override:
//
//   `detailed.maxBlocksPerGroup` is 8 (not 12), because the spec is
//   explicit that 8 is the hard ceiling for visual readability. The
//   previous repoToWorkflow value of 12 has been reconciled here.

export type DetailLevelKey = 'overview' | 'standard' | 'detailed';

export interface DetailLevelSpec {
  key: DetailLevelKey;
  /** UI label (Swedish — matches the dialog). */
  labelSv: string;
  /** Hard cap on visible blocks per group. */
  maxBlocksPerGroup: number;
  /** Folder buckets with ≥ this many files collapse to one block. */
  folderCollapseThreshold: number;
  /** Soft hint for AI / docs — "blocks per group". */
  blocksPerGroupHint: number;
}

export const DETAIL_LEVELS: Readonly<Record<DetailLevelKey, DetailLevelSpec>> = {
  overview: {
    key: 'overview',
    labelSv: 'Översikt',
    maxBlocksPerGroup: 4,
    folderCollapseThreshold: 3,
    blocksPerGroupHint: 3,
  },
  standard: {
    key: 'standard',
    labelSv: 'Standard',
    maxBlocksPerGroup: 6,
    folderCollapseThreshold: 5,
    blocksPerGroupHint: 5,
  },
  detailed: {
    key: 'detailed',
    labelSv: 'Detaljerad',
    maxBlocksPerGroup: 8,
    folderCollapseThreshold: 7,
    blocksPerGroupHint: 8,
  },
};

// ── Layout grid ──────────────────────────────────────────────────────

export const LAYOUT_GRID = {
  /** Coordinate snap — all x/y must be multiples of this. */
  SNAP: 20,
  /** Default block width — used for spacing math. */
  BLOCK_W: 200,
  /** Default block height — used for spacing math. */
  BLOCK_H: 80,
  /** Horizontal pitch between blocks in a row. */
  BLOCK_DX: 280,
  /** Vertical pitch between subsystem strips. */
  STRIP_DY: 240,
  /** Vertical pitch between rows within one strip. */
  STRIP_INNER_DY: 140,
  /** Default system-root block X (single-sheet ring layout). */
  SYSTEM_X: 0,
  /** Default system-root block Y (single-sheet ring layout). */
  SYSTEM_Y: -480,
} as const;

/** Snap a value to the grid. */
export function snapToGrid(v: number): number {
  return Math.round(v / LAYOUT_GRID.SNAP) * LAYOUT_GRID.SNAP;
}

// ── Sizing thresholds ────────────────────────────────────────────────
//
// "When to layer / split" — straight from the handover spec §7.

export interface SizingTier {
  upTo: number;          // file/block count upper bound (Infinity for last)
  strategy: string;      // human-readable
}

export const SIZING_TIERS: readonly SizingTier[] = [
  { upTo: 6,   strategy: 'flat single sheet, no groups' },
  { upTo: 15,  strategy: 'single sheet, visual groups' },
  { upTo: 30,  strategy: 'single sheet with layers (col:true), 4-6 of them' },
  { upTo: Infinity, strategy: 'multi-sheet (lean-1), one sheet per subsystem' },
];

/** Auto-mode threshold: <50 source files → single-sheet, else multi. */
export const AUTO_SINGLE_SHEET_MAX_FILES = 50;

// ── Hard rules (for the §6 self-check rendering) ─────────────────────

export interface SchemaRule {
  /** Stable id — also used as a snapshot-test key. */
  id: string;
  /** Short label in the dialog checklist. */
  label: string;
  /** Full prose for the AI prompt. */
  prose: string;
  /** Which schema(s) the rule applies to. */
  scope: 'both' | 'compact' | 'lean-1';
}

export const SCHEMA_RULES: readonly SchemaRule[] = [
  {
    id: 'unique-ids',
    label: 'All IDs globally unique',
    prose: 'All IDs (blocks, connectors, groups, sheets) must be globally unique within the document.',
    scope: 'both',
  },
  {
    id: 'endpoints-exist',
    label: 'Every connector endpoint exists',
    prose: 'Every connector `from`/`to` (and cross-sheet `from`/`to`) references a real **block** id.',
    scope: 'both',
  },
  {
    id: 'lean1-no-group-endpoints',
    label: 'Lean-1 connectors target blocks, NOT groups',
    prose: 'In lean-1, connectors may reference **blocks only**. Pointing a connector at a group id rejects 1 line per occurrence ("id saknas i blocks"). Compact mode allows layer ids; lean-1 does not.',
    scope: 'lean-1',
  },
  {
    id: 'port-uniqueness',
    label: 'Port-uniqueness per block-pair',
    prose: 'Between any pair A↔B, each (block, port) anchors at most ONE connector. `A.t→B.t` plus `A.b→B.t` is illegal because B.t is reused. Across different pairs the same source port is fine.',
    scope: 'both',
  },
  {
    id: 'ports-present',
    label: 'fp/tp present + match relative position',
    prose: 'Every connector has both `fp`/`tp` (compact) or `fromPort`/`toPort` (lean-1), and the chosen pair matches the relative position of the two blocks.',
    scope: 'both',
  },
  {
    id: 'grid-snap',
    label: 'x/y are multiples of 20',
    prose: 'All `x`/`y` coordinates must be multiples of 20 (GRID_SIZE). Block (x,y) is the CENTER of the block, not top-left.',
    scope: 'both',
  },
  {
    id: 'layer-positioning',
    label: 'col:true layers have cx + cy; pg valid',
    prose: 'Compact: every `col:true` layer has `cx` and `cy`. `pg` is either a valid group/layer id or `null`. Each block is in ≤ 1 group.',
    scope: 'compact',
  },
  {
    id: 'adjacent-cross',
    label: 'Cross-sheet only between adjacent sheets (|Δ|=1)',
    prose: 'Lean-1: cross-sheet connectors only between adjacent sheets — `|fromSheet - toSheet| === 1`. Compact-multi: same rule on `fsi`/`tsi`.',
    scope: 'both',
  },
  {
    id: 'colors-and-icons',
    label: 'Colors follow palette; icons match format casing',
    prose: 'Use a single hex per domain across the whole document. Compact icons are lowercase-kebab (`git-branch`); lean-1 icons are PascalCase (`GitBranch`). Both are lucide names.',
    scope: 'both',
  },
  {
    id: 'raw-json',
    label: 'Output is raw JSON only',
    prose: 'No markdown fences, no prose, no `undefined` literals (use `null` or omit), no waypoint arrays.',
    scope: 'both',
  },
  {
    id: 'co-center',
    label: '`co: "center"` at root',
    prose: 'Compact root MUST include `"co":"center"`. The coordinate origin is always the center of each block.',
    scope: 'compact',
  },
  {
    id: 'block-membership',
    label: 'Each block in at most one group',
    prose: 'A block id appears in at most one group/layer `bIds`/`blockIds` list. Duplicates reject on import.',
    scope: 'both',
  },
];

// ── Operating procedure (handover §1) ────────────────────────────────
//
// The 4-phase loop that the AI / human cartographer should follow.

export const OPERATING_PROCEDURE = [
  {
    phase: 'A',
    name: 'Explore',
    bullets: [
      'List the tree first (depth ~3) — the folder layout IS the architecture.',
      'Count source files (exclude node_modules, lockfiles, generated dirs, binaries).',
      'Read docs before code: documentation/, architecture/, README*, ADRs.',
      'Read only spine files: entry points, route tables, API contracts, DB schema, service config. Sample, never slurp.',
      'Skip generated files, vendored code, fixtures.',
    ],
  },
  {
    phase: 'B',
    name: 'Plan',
    bullets: [
      'Write a plain-text outline first: SUBSYSTEM → GROUPS → KEY BLOCKS (each tied to real paths).',
      'Decide the domain → color mapping now (use the locked palette).',
      'Pick the schema based on Scan-läge (single/multi/auto) + source-file count.',
    ],
  },
  {
    phase: 'C',
    name: 'Emit',
    bullets: [
      'Use the chosen schema (compact §2 or lean-1 §3) and obey the layout grammar.',
      'Main flow left → right; sub-flows top → bottom.',
      'Pick the port that matches the RELATIVE position (no long-detour beziers).',
      'Output raw JSON only.',
    ],
  },
  {
    phase: 'D',
    name: 'Validate',
    bullets: [
      'Run every self-check item.',
      'Fix and re-emit until clean — iterate against the validator.',
      'In lean-1 specifically: connectors target blocks only, never group ids (§6.1 mistake).',
    ],
  },
] as const;
